// WelcomeScreen.jsx – Startbildschirm für nicht eingeloggte Besucher.
// Ablauf Login: E-Mail eingeben → Code per Mail → Code eingeben → eingeloggt (onLogin).
// Wird auch vom Dashboard aus für „Neuer Stammbaum“ verwendet (dann nur Name eingeben).
// Jeder Bildschirm ist ein „Schritt“ (step); je nach Schritt wird unten ein anderer
// Inhalt gezeigt.
import React, { useState } from 'react';
import './WelcomeScreen.css';

const API = 'https://editor.bandeleart.ch';
// Link zur Datenschutzerklärung auf der BandeleArt-Webseite.
const PRIVACY_URL = 'https://bandeleart.ch/datenschutzbestimmungen';

// Die möglichen Schritte. NEW_EMAIL = neuer Stammbaum (nur für Eingeloggte, vom Dashboard aus).
const STEPS = {
  HOME: 'home',
  NEW_EMAIL: 'new_email',
  LOGIN_EMAIL: 'login_email',
  LOGIN_CODE: 'login_code',
};

// Checkbox „Datenschutzbestimmungen gelesen“ – erscheint nur, wenn der Server es verlangt
// (neue E-Mail oder neue Version der Datenschutzerklärung).
function PrivacyConsent({ checked, onChange }) {
  return (
    <div style={{ margin: '12px 0', padding: '12px 14px', background: '#f5f7ff', borderRadius: 8, fontSize: 14, lineHeight: 1.5 }}>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ marginTop: 3 }} />
        <span>
          Ich habe die{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Datenschutzbestimmungen</a>{' '}
          gelesen und bin damit einverstanden. Ich erfasse nur Daten und Fotos von Personen,
          für die ich die Erlaubnis habe.
        </span>
      </label>
    </div>
  );
}

// Fortschrittsanzeige 1 – 2 – 3 oben im Fenster.
function StepIndicator({ steps, current }) {
  return (
    <div className="ws-steps">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <div className={`ws-step ${i === current ? 'ws-step--active' : i < current ? 'ws-step--done' : ''}`}>
            {i < current ? '✓' : i + 1}
          </div>
          {i < steps.length - 1 && <div className={`ws-step-line ${i < current ? 'ws-step-line--done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// Props: onStart (neuer Stammbaum angelegt), onLogin (eingeloggt), onCancel (zurück
// zum Dashboard), initialStep, userEmail/userToken (bereits eingeloggt),
// magicLogin (E-Mail + Code aus dem Link in der Mail), prefillEmail (aus einer Einladung).
export function WelcomeScreen({ onStart, onLogin, onCancel, initialStep, userEmail, userToken, magicLogin, prefillEmail }) {
  const [step, setStep] = useState(magicLogin ? STEPS.LOGIN_CODE : prefillEmail ? STEPS.LOGIN_EMAIL : (initialStep || STEPS.HOME));
  const [name, setName] = useState('');
  const [code, setCode] = useState(magicLogin?.code || '');
  const [loginEmail, setLoginEmail] = useState(magicLogin?.email || prefillEmail || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [isNewLogin, setIsNewLogin] = useState(false); // „Neues Login erstellen“ statt „Ich habe bereits ein Login“
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const clearError = () => setError('');

  // ── Neuer Stammbaum ──────────────────────────────────────────────────

  // Neuer Stammbaum (Eingeloggte, vom Dashboard): direkt anlegen, ohne Code.
  const handleNewEmailSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (!name.trim()) { setError('Bitte einen Namen für den Stammbaum eingeben.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/tree/new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userToken}` },
        body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) { setError('Dein Login ist abgelaufen. Bitte melde dich neu an.'); return; }
      if (!res.ok) { setError(data.error || 'Fehler beim Erstellen.'); return; }
      onStart({ isNew: true, email: userEmail, treeCode: data.treeCode, treeName: data.treeName || name, keepLoginTime: true });
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // ── Login (bestehender Account) ──────────────────────────────────────

  // Login Schritt 1: Code an die E-Mail schicken lassen.
  const handleLoginEmailSubmit = async (e) => {
    e.preventDefault();
    clearError();
    if (!loginEmail.includes('@')) { setError('Bitte eine gültige E-Mail-Adresse eingeben.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Fehler beim Senden.'); return; }
      setStep(STEPS.LOGIN_CODE);
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // Login Schritt 2: Code prüfen. Verlangt der Server die Datenschutz-Bestätigung,
  // erscheint die Checkbox und man muss nochmals auf „Weiter“ tippen.
  const handleLoginCodeSubmit = async (e) => {
    if (e) e.preventDefault();
    clearError();
    if (code.length !== 6) { setError('Bitte den 6-stelligen Code eingeben.'); return; }
    if (needsConsent && !privacyAccepted) { setError('Bitte bestätige die Datenschutzbestimmungen.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, code, privacyAccepted }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.needsConsent) { setNeedsConsent(true); setError('Bitte bestätige noch die Datenschutzbestimmungen.'); return; }
        setError(data.error || 'Ungültiger Code.'); return;
      }
      onLogin({ email: loginEmail, token: data.token, trees: data.trees || [] });
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // „Direkt einloggen“ aus der Mail: Code automatisch prüfen (einmalig)
  const magicTriedRef = React.useRef(false);
  React.useEffect(() => {
    if (magicLogin && !magicTriedRef.current) {
      magicTriedRef.current = true;
      handleLoginCodeSubmit();
    }
  }, []); // eslint-disable-line

  // Neuen Code anfordern. Meldet der Server ein Problem (z.B. zu viele Codes pro Stunde),
  // wird es angezeigt; sonst der Hinweis, dass ein neuer Code unterwegs ist.
  const resendLoginCode = async () => {
    setCode('');
    clearError();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail }),
      });
      const data = await res.json().catch(() => ({}));
      setError(res.ok ? 'Ein neuer Code wurde verschickt.' : (data.error || 'Fehler beim Senden.'));
    } catch {
      setError('Server nicht erreichbar. Bitte später versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  if (step === STEPS.HOME) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <div className="ws-logo">Stammbaum-Memo</div>
          <p className="ws-subtitle">Erstelle deinen persönlichen Familien-Stammbaum</p>
          <div className="ws-options">
            <button className="ws-option ws-option--primary" onClick={() => { setIsNewLogin(true); setStep(STEPS.LOGIN_EMAIL); clearError(); setCode(''); }}>
              <span className="ws-option-icon">✦</span>
              <span className="ws-option-title">Neues Login erstellen</span>
              <span className="ws-option-desc">Melde dich mit deiner E-Mail-Adresse an und erstelle danach deinen Stammbaum</span>
            </button>
            {onLogin && (
              <button className="ws-option" onClick={() => { setIsNewLogin(false); setStep(STEPS.LOGIN_EMAIL); clearError(); setCode(''); }}>
                <span className="ws-option-icon">↗</span>
                <span className="ws-option-title">Ich habe bereits ein Login</span>
                <span className="ws-option-desc">Öffne deine Stammbäume mit deiner E-Mail-Adresse</span>
              </button>
            )}
          </div>
          {onCancel && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="ws-btn ws-btn--secondary" onClick={onCancel}>← Zurück zu meinen Stammbäumen</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === STEPS.NEW_EMAIL) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <h2 className="ws-title">Neuer Stammbaum</h2>
          <p className="ws-desc">
            Wie soll dein neuer Stammbaum heissen? Er wird mit <strong>{userEmail}</strong> verknüpft.
          </p>
          <form onSubmit={handleNewEmailSubmit}>
            <input
              className="ws-input"
              type="text"
              placeholder="Name des Stammbaums (z.B. Familie Müller)"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
            />
            {error && <p className="ws-error">{error}</p>}
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => (onCancel ? onCancel() : setStep(STEPS.HOME))}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loading ? 'Wird erstellt…' : 'Stammbaum erstellen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.LOGIN_EMAIL) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <StepIndicator steps={['E-Mail', 'Bestätigung']} current={0} />
          <h2 className="ws-title">{isNewLogin ? 'Neues Login erstellen' : 'Einloggen'}</h2>
          <p className="ws-desc">
            {isNewLogin
              ? 'Gib deine E-Mail-Adresse ein. Wir senden dir einen Bestätigungscode. Danach kannst du deinen ersten Stammbaum erstellen.'
              : 'Gib deine E-Mail-Adresse ein. Wir senden dir einen Einmalcode zum Einloggen.'}
          </p>
          <form onSubmit={handleLoginEmailSubmit}>
            <input
              className="ws-input"
              type="email"
              placeholder="deine@email.ch"
              value={loginEmail}
              onChange={e => setLoginEmail(e.target.value)}
              autoFocus
            />
            {error && <p className="ws-error">{error}</p>}
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => setStep(STEPS.HOME)}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loading ? 'Wird gesendet…' : 'Code senden'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (step === STEPS.LOGIN_CODE) {
    return (
      <div className="ws-overlay">
        <div className="ws-card">
          <StepIndicator steps={['E-Mail', 'Bestätigung']} current={1} />
          <h2 className="ws-title">Code bestätigen</h2>
          <p className="ws-desc">
            Wir haben einen 6-stelligen Einmalcode an <strong>{loginEmail}</strong> gesendet.
          </p>
          <form onSubmit={handleLoginCodeSubmit}>
            <input
              className="ws-input ws-input--code"
              type="text"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
            />
            {needsConsent && <PrivacyConsent checked={privacyAccepted} onChange={v => { setPrivacyAccepted(v); if (v) clearError(); }} />}
            {error && <p className="ws-error">{error}</p>}
            <p className="ws-hint" onClick={resendLoginCode} style={{cursor:'pointer'}}>
              Keinen Code erhalten? Erneut senden
            </p>
            <div className="ws-actions">
              <button type="button" className="ws-btn ws-btn--secondary" onClick={() => setStep(STEPS.LOGIN_EMAIL)}>Zurück</button>
              <button type="submit" className="ws-btn ws-btn--primary" disabled={loading}>
                {loading ? 'Wird geprüft…' : (isNewLogin ? 'Login erstellen' : 'Einloggen')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
}
